/**
 * Trade transaction monitor — every closed trade with entry/exit logic,
 * dollars risked vs gained, stop-loss adherence, and on-demand AI insights
 * (generated asynchronously so autonomy stays hot).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Shield,
} from 'lucide-react';
import { tradeMonitoringApi, ApiError } from '../lib/api';
import { ClickableTicker } from '../components/TradingViewModal';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

type StopAdherence = {
  status?: string;
  breach?: boolean;
  leak_dollars?: number;
  planned_stop_price?: number;
  notes?: string;
};

type TradeRow = {
  exit_log_id?: string;
  timestamp?: string;
  symbol?: string;
  vertical?: string;
  side?: string;
  quantity?: number;
  entry?: { summary?: string; fields?: Record<string, unknown> };
  exit?: { summary?: string; fields?: Record<string, unknown> };
  execution?: Record<string, unknown> | null;
  pnl?: { realized_pnl?: number; realized_pnl_pct?: number };
  risk?: {
    dollars_risked_to_initial_stop?: number | null;
    notional_at_entry?: number;
    dollars_gained?: number;
  };
  stop_loss_adherence?: StopAdherence;
  ai_insight?: { insight_text?: string; updated_at?: string } | null;
};

type SLReport = {
  trade_count?: number;
  breach_count?: number;
  unknown_adherence_count?: number;
  total_sl_leak_dollars?: number;
  total_realized_pnl_dollars?: number;
  total_dollars_risked_to_initial_stop?: number | null;
};

function ExpandedTradeDetail({ row }: { row: TradeRow }) {
  const ad = row.stop_loss_adherence;
  const fields = row.entry?.fields ?? {};
  const execFields = row.execution ?? {};
  const aiReasoning = fields.ai_reasoning;
  const plannedStop = fields.planned_stop as number | null | undefined;
  const criteriaMatched = fields.criteria_matched as number | null | undefined;
  const criteriaTotal  = fields.criteria_total  as number | null | undefined;
  const entryOrderId   = fields.entry_order_id  as string | null | undefined;
  const exitOrderId    = (execFields as Record<string,unknown>).exit_order_id as string | null | undefined;

  return (
    <div className="p-4 space-y-4 text-sm">
      {/* ── Entry ── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Entry</div>
          <p className="text-foreground/90 leading-snug">{row.entry?.summary || '—'}</p>
          {aiReasoning != null && String(aiReasoning).trim() !== '' ? (
            <p className="mt-1.5 text-muted-foreground text-[11px] whitespace-pre-wrap italic">
              Signal AI: {String(aiReasoning).slice(0, 300)}{String(aiReasoning).length > 300 ? '…' : ''}
            </p>
          ) : null}
          {criteriaMatched != null && criteriaTotal != null ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Criteria: <span className="font-mono text-foreground">{criteriaMatched}/{criteriaTotal}</span> matched
            </p>
          ) : null}
          {entryOrderId ? (
            <p className="mt-1 text-[10px] text-muted-foreground font-mono">
              Entry order: {String(entryOrderId).slice(0, 16)}…
            </p>
          ) : null}
        </div>

        {/* ── Exit ── */}
        <div>
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Exit</div>
          <p className="text-foreground/90 leading-snug">{row.exit?.summary || '—'}</p>
          {exitOrderId ? (
            <p className="mt-1 text-[10px] text-muted-foreground font-mono">
              Exit order: {String(exitOrderId).slice(0, 16)}…
            </p>
          ) : null}
          {(execFields as Record<string,unknown>).slippage_vs_planned_stop != null ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Slippage vs planned stop:{' '}
              <span className="font-mono text-foreground">
                ${Number((execFields as Record<string,unknown>).slippage_vs_planned_stop).toFixed(4)}
              </span>
            </p>
          ) : null}
        </div>
      </div>

      {/* ── Risk detail ── */}
      <div className="rounded-md border border-border bg-muted/30 px-3 py-2 grid grid-cols-3 gap-3 text-[11px]">
        <div>
          <div className="text-muted-foreground">Notional at entry</div>
          <div className="font-mono font-semibold text-foreground">
            {row.risk?.notional_at_entry != null ? `$${row.risk.notional_at_entry.toLocaleString()}` : '—'}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Planned stop</div>
          <div className="font-mono font-semibold text-foreground">
            {plannedStop != null && plannedStop > 0 ? `$${plannedStop.toFixed(4)}` : '—'}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">$ risked to stop</div>
          <div className="font-mono font-semibold text-foreground">
            {row.risk?.dollars_risked_to_initial_stop != null
              ? `$${Number(row.risk.dollars_risked_to_initial_stop).toFixed(2)}`
              : '—'}
          </div>
        </div>
      </div>

      {/* ── SL adherence notes ── */}
      {ad?.notes ? (
        <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 rounded px-2 py-1">
          {ad.notes}
        </p>
      ) : null}

      {/* ── AI Insight ── */}
      {row.ai_insight?.insight_text ? (
        <div className="rounded-lg border bg-background/80 p-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> AI insight
          </div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{row.ai_insight.insight_text}</p>
          {row.ai_insight.updated_at ? (
            <p className="text-[10px] text-muted-foreground mt-2">{row.ai_insight.updated_at}</p>
          ) : null}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          No AI insight yet — click &quot;AI insights (this page)&quot; to queue background analysis.
        </p>
      )}
    </div>
  );
}

const TradeTransactionMonitor: React.FC = () => {
  const [days, setDays] = useState(30);
  const [vertical, setVertical] = useState<string>('all');
  const [breachesOnly, setBreachesOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 40;
  const [rows, setRows] = useState<TradeRow[]>([]);
  const [slReport, setSlReport] = useState<SLReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [insightBusy, setInsightBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const txParams = new URLSearchParams();
      txParams.set('days', String(days));
      txParams.set('limit', String(limit));
      txParams.set('offset', String(offset));
      if (vertical !== 'all') txParams.set('vertical', vertical);
      txParams.set('breaches_only', breachesOnly ? 'true' : 'false');

      const rpParams = new URLSearchParams();
      rpParams.set('days', String(days));
      rpParams.set('limit', '800');
      if (vertical !== 'all') rpParams.set('vertical', vertical);

      const [txRes, rpRes] = await Promise.all([
        tradeMonitoringApi.transactions(txParams) as Promise<{ transactions?: TradeRow[] }>,
        tradeMonitoringApi.stopLossReport(rpParams) as Promise<SLReport>,
      ]);
      setRows(txRes.transactions || []);
      setSlReport(rpRes);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [days, vertical, breachesOnly, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const queuePageInsights = async () => {
    const ids = rows.map((r) => r.exit_log_id).filter(Boolean) as string[];
    if (!ids.length) return;
    setInsightBusy(true);
    setError(null);
    try {
      await tradeMonitoringApi.queueInsights(ids);
      setTimeout(() => void load(), 4000);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError(e instanceof Error ? e.message : 'Queue failed');
    } finally {
      setInsightBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Trade transactions</h1>
          {rows.length > 0 && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
              (rows[0] as TradeRow & { source?: string }).source === 'alpaca'
                ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                : 'bg-muted text-muted-foreground border-border'
            }`}>
              {(rows[0] as TradeRow & { source?: string }).source === 'alpaca'
                ? '✓ Alpaca verbatim'
                : 'Decision logs'}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Each row is a closed position pulled directly from Alpaca: entry via the original bot signal,
          exit reasoning, dollars risked to the initial stop versus realized outcome, and stop-loss
          adherence versus the planned signal stop. AI commentary generates only on request and runs
          in the background so live autonomy is unaffected.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {slReport && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Trades</div>
            <div className="text-2xl font-semibold mt-1">{slReport.trade_count ?? 0}</div>
          </div>
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" /> SL breaches
            </div>
            <div className="text-2xl font-semibold mt-1 text-amber-600 dark:text-amber-400">
              {slReport.breach_count ?? 0}
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">SL leak ($)</div>
            <div className="text-2xl font-semibold mt-1">
              {(slReport.total_sl_leak_dollars ?? 0).toFixed(2)}
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Realized P&amp;L ($)</div>
            <div className="text-2xl font-semibold mt-1">
              {(slReport.total_realized_pnl_dollars ?? 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Period</label>
          <Select value={String(days)} onValueChange={(v) => { setOffset(0); setDays(Number(v)); }}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="180">180 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Vertical</label>
          <Select value={vertical} onValueChange={(v) => { setOffset(0); setVertical(v); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="stocks">Stocks</SelectItem>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="forex">Forex</SelectItem>
              <SelectItem value="futures">Futures</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Filter</label>
          <Select
            value={breachesOnly ? 'breach' : 'all'}
            onValueChange={(v) => { setOffset(0); setBreachesOnly(v === 'breach'); }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All trades</SelectItem>
              <SelectItem value="breach">Stop-loss breaches only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button variant="secondary" size="sm" onClick={() => void queuePageInsights()} disabled={insightBusy || !rows.length}>
          <Sparkles className="w-4 h-4 mr-2" />
          {insightBusy ? 'Queued…' : 'AI insights (this page)'}
        </Button>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Time</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead>P&amp;L</TableHead>
              <TableHead>Risk @ SL</TableHead>
              <TableHead>SL adherence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                  No closed trades in this window.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r, idx) => {
              const id = r.exit_log_id || r.timestamp || `row-${idx}`;
              const open = !!expanded[id];
              const ad = r.stop_loss_adherence;
              const breach = ad?.breach;
              return (
                <React.Fragment key={id}>
                  <TableRow className={breach ? 'bg-amber-500/5' : undefined}>
                    <TableCell className="align-top">
                      <button
                        type="button"
                        className="p-1 rounded-md hover:bg-muted text-muted-foreground"
                        onClick={() => toggle(id)}
                        aria-expanded={open}
                      >
                        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </TableCell>
                    <TableCell className="align-top text-xs whitespace-nowrap">
                      {r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="align-top font-medium">
                      {r.symbol ? <ClickableTicker symbol={r.symbol} /> : '—'}
                      <div className="text-[11px] text-muted-foreground font-normal">
                        {r.vertical || ''} · {r.side || ''} ×{r.quantity ?? '—'}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <span className={(r.pnl?.realized_pnl ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                        {(r.pnl?.realized_pnl ?? 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="align-top text-sm">
                      {r.risk?.dollars_risked_to_initial_stop != null
                        ? r.risk.dollars_risked_to_initial_stop.toFixed(2)
                        : '—'}
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-0.5">
                        {ad?.status === 'unknown' && (
                          <Badge variant="secondary">Unknown</Badge>
                        )}
                        {ad?.status === 'ok' && (
                          <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300">OK</Badge>
                        )}
                        {breach && (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Breach
                          </Badge>
                        )}
                        {typeof ad?.leak_dollars === 'number' && ad.leak_dollars > 0 && (
                          <div className="text-[11px] text-muted-foreground">
                            Leak ${ad.leak_dollars.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={6} className="p-0">
                        <ExpandedTradeDetail row={r} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center text-sm text-muted-foreground">
        <Button
          variant="ghost"
          size="sm"
          disabled={offset === 0 || loading}
          onClick={() => setOffset((o) => Math.max(0, o - limit))}
        >
          Previous
        </Button>
        <span>
          Page {Math.floor(offset / limit) + 1}
          {rows.length >= limit ? ' — more may be available' : ''}
        </span>
        <Button
          variant="ghost"
          size="sm"
          disabled={rows.length < limit || loading}
          onClick={() => setOffset((o) => o + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default TradeTransactionMonitor;
